import React, { useState, useRef, useEffect } from 'react';
import { Select } from 'antd';
import './PhoneInput.css';

const { Option } = Select;

const COUNTRIES = [
    { code: '375', name: 'Беларусь', flagImg: 'by_flag.png', format: '+375 (XX) XXX-XX-XX', maxDigits: 9 },
    { code: '7', name: 'Россия', flagImg: 'ru_flag.png', format: '+7 (XXX) XXX-XX-XX', maxDigits: 10 },
];

const PhoneInput = ({ value, onChange, placeholder, className, ...props }) => {
    const [displayValue, setDisplayValue] = useState('+375 (__) ___-__-__');
    const [cursorPosition, setCursorPosition] = useState(null);
    const [countryCode, setCountryCode] = useState('375'); // 375 для Беларуси, 7 для России
    const inputRef = useRef(null);

    useEffect(() => {
        if (value) {
            // Определяем код страны из значения
            let digits = value.replace(/\D/g, '');
            let detectedCountryCode = '375';

            if (digits.startsWith('375')) {
                detectedCountryCode = '375';
                digits = digits.slice(3);
            } else if (digits.startsWith('7')) {
                detectedCountryCode = '7';
                digits = digits.slice(1);
            }

            setCountryCode(detectedCountryCode);
            setDisplayValue(formatPhoneNumber(digits, detectedCountryCode));
        } else {
            setDisplayValue('+375 (__) ___-__-__');
            setCountryCode('375');
        }
    }, [value]);

    useEffect(() => {
        if (cursorPosition !== null && inputRef.current) {
            inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
            setCursorPosition(null);
        }
    }, [displayValue, cursorPosition]);

    const formatPhoneNumber = (digits, countryCode = '7') => {
        if (!digits || digits.length === 0) {
            return countryCode === '375' ? '+375 (__) ___-__-__' : '+7 (___) ___-__-__';
        }

        if (countryCode === '375') {
            // Белорусский формат: +375 (XX) XXX-XX-XX (9 цифр)
            const limitedDigits = digits.slice(0, 9);
            const paddedDigits = limitedDigits.padEnd(9, '_');
            return `+375 (${paddedDigits.slice(0, 2)}) ${paddedDigits.slice(2, 5)}-${paddedDigits.slice(5, 7)}-${paddedDigits.slice(7, 9)}`;
        } else {
            // Российский формат: +7 (XXX) XXX-XX-XX (10 цифр)
            const limitedDigits = digits.slice(0, 10);
            const paddedDigits = limitedDigits.padEnd(10, '_');
            return `+7 (${paddedDigits.slice(0, 3)}) ${paddedDigits.slice(3, 6)}-${paddedDigits.slice(6, 8)}-${paddedDigits.slice(8, 10)}`;
        }
    };

    const getEditablePositions = () => {
        if (countryCode === '375') {
            // Белорусский формат: +375 (XX) XXX-XX-XX
            return [6, 7, 9, 10, 11, 13, 14, 16, 17];
        } else {
            // Российский формат: +7 (XXX) XXX-XX-XX
            return [4, 5, 6, 8, 9, 10, 12, 13, 15, 16];
        }
    };

    const getDigitPositions = () => {
        if (countryCode === '375') {
            // Позиции цифр в белорусском формате
            return {
                0: 6,  // первая цифра
                1: 7,  // вторая цифра
                2: 9,  // третья цифра
                3: 10, // четвертая цифра
                4: 11, // пятая цифра
                5: 13, // шестая цифра
                6: 14, // седьмая цифра
                7: 16, // восьмая цифра
                8: 17  // девятая цифра
            };
        } else {
            // Позиции цифр в российском формате
            return {
                0: 4,  // первая цифра
                1: 5,  // вторая цифра
                2: 6,  // третья цифра
                3: 8,  // четвертая цифра
                4: 9,  // пятая цифра
                5: 10, // шестая цифра
                6: 12, // седьмая цифра
                7: 13, // восьмая цифра
                8: 15, // девятая цифра
                9: 16  // десятая цифра
            };
        }
    };

    const findNearestEditablePosition = (position) => {
        const editablePositions = getEditablePositions();

        // Если позиция уже редактируемая
        if (editablePositions.includes(position)) {
            return position;
        }

        // Ищем ближайшую редактируемую позицию
        let leftPos = position - 1;
        let rightPos = position;

        while (leftPos >= 0 && !editablePositions.includes(leftPos)) {
            leftPos--;
        }

        while (rightPos < displayValue.length && !editablePositions.includes(rightPos)) {
            rightPos++;
        }

        if (leftPos >= 0 && rightPos < displayValue.length) {
            return (position - leftPos) < (rightPos - position) ? leftPos : rightPos;
        } else if (leftPos >= 0) {
            return leftPos;
        } else if (rightPos < displayValue.length) {
            return rightPos;
        }

        return countryCode === '375' ? 6 : 4; // fallback to first editable position
    };

    const handleInput = (e) => {
        const inputValue = e.target.value;
        const selectionStart = e.target.selectionStart;

        // Определяем минимальную позицию для редактирования в зависимости от кода страны
        const minEditPosition = countryCode === '375' ? 6 : 4;

        // Если пользователь пытается изменить префикс - блокируем
        if (selectionStart < minEditPosition) {
            setCursorPosition(minEditPosition);
            return;
        }

        // Получаем текущие цифры (без форматирования)
        const currentDigits = displayValue.replace(/\D/g, '').slice(countryCode.length);
        const newDigits = inputValue.replace(/\D/g, '').slice(countryCode.length);

        // Определяем, была ли это вставка цифры или удаление
        const isDeletion = newDigits.length < currentDigits.length;

        let digitsToProcess;
        const maxDigits = countryCode === '375' ? 9 : 10;

        if (isDeletion) {
            // При удалении используем новые цифры
            digitsToProcess = newDigits;
        } else {
            // При вводе берем максимальную длину
            digitsToProcess = newDigits.slice(0, maxDigits);
        }

        const formatted = formatPhoneNumber(digitsToProcess, countryCode);
        setDisplayValue(formatted);

        // Вызываем onChange с полным номером
        if (onChange) {
            onChange(digitsToProcess.length > 0 ? `+${countryCode}${digitsToProcess}` : '');
        }

        // Управление позицией курсора
        if (isDeletion) {
            // При удалении - перемещаем курсор к текущей позиции или ближайшей редактируемой
            const newPosition = findNearestEditablePosition(selectionStart);
            setCursorPosition(newPosition);
        } else {
            // При вводе - перемещаем курсор вперед к следующей редактируемой позиции
            const newPosition = findNearestEditablePosition(selectionStart + 1);
            setCursorPosition(newPosition);
        }
    };

    const handleKeyDown = (e) => {
        const selectionStart = e.target.selectionStart;
        const minEditPosition = countryCode === '375' ? 6 : 4;

        // Запрещаем редактирование префикса
        if (selectionStart < minEditPosition &&
            !['ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key)) {
            e.preventDefault();
            setCursorPosition(minEditPosition);
            return;
        }

        // Разрешаем только цифры, Backspace, Delete, стрелки, Tab
        if (!/[0-9]/.test(e.key) &&
            !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key)) {
            e.preventDefault();
        }

        // Обработка Backspace
        if (e.key === 'Backspace') {
            e.preventDefault(); // Предотвращаем стандартное поведение

            const currentDigits = displayValue.replace(/\D/g, '').slice(countryCode.length);
            const digitPositions = getDigitPositions();
            const maxDigitIndex = countryCode === '375' ? 8 : 9;

            // Находим позицию цифры, которую нужно удалить
            let digitIndexToDelete = -1;
            for (let i = maxDigitIndex; i >= 0; i--) {
                if (currentDigits[i] !== undefined) {
                    if (selectionStart > digitPositions[i]) {
                        digitIndexToDelete = i;
                        break;
                    }
                }
            }

            // Если не нашли цифру для удаления, удаляем последнюю
            if (digitIndexToDelete === -1 && currentDigits.length > 0) {
                digitIndexToDelete = currentDigits.length - 1;
            }

            if (digitIndexToDelete >= 0) {
                const newDigits = currentDigits.slice(0, digitIndexToDelete) + currentDigits.slice(digitIndexToDelete + 1);
                const formatted = formatPhoneNumber(newDigits, countryCode);
                setDisplayValue(formatted);

                if (onChange) {
                    onChange(newDigits.length > 0 ? `+${countryCode}${newDigits}` : '');
                }

                // Устанавливаем курсор на позицию удаленной цифры
                const newCursorPos = digitPositions[Math.min(digitIndexToDelete, maxDigitIndex)];
                setCursorPosition(newCursorPos);
            }
        }

        // Обработка Delete
        if (e.key === 'Delete') {
            e.preventDefault(); // Предотвращаем стандартное поведение

            const currentDigits = displayValue.replace(/\D/g, '').slice(countryCode.length);
            const digitPositions = getDigitPositions();
            const maxDigitIndex = countryCode === '375' ? 8 : 9;

            // Находим позицию цифры, которую нужно удалить
            let digitIndexToDelete = -1;
            for (let i = 0; i <= maxDigitIndex; i++) {
                if (currentDigits[i] !== undefined) {
                    if (selectionStart <= digitPositions[i]) {
                        digitIndexToDelete = i;
                        break;
                    }
                }
            }

            if (digitIndexToDelete >= 0) {
                const newDigits = currentDigits.slice(0, digitIndexToDelete) + currentDigits.slice(digitIndexToDelete + 1);
                const formatted = formatPhoneNumber(newDigits, countryCode);
                setDisplayValue(formatted);

                if (onChange) {
                    onChange(newDigits.length > 0 ? `+${countryCode}${newDigits}` : '');
                }

                // Устанавливаем курсор на позицию удаленной цифры
                const newCursorPos = digitPositions[Math.min(digitIndexToDelete, maxDigitIndex)];
                setCursorPosition(newCursorPos);
            }
        }
    };

    const handleClick = (e) => {
        if (!inputRef.current) return;

        const clickPos = e.target.selectionStart;
        const minEditPosition = countryCode === '375' ? 6 : 4;

        // Если кликнули не на редактируемую позицию, перемещаем курсор к ближайшей
        if (clickPos < minEditPosition || !getEditablePositions().includes(clickPos)) {
            const nearestPos = findNearestEditablePosition(clickPos);
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.setSelectionRange(nearestPos, nearestPos);
                }
            }, 0);
        }
    };

    const handleFocus = (e) => {
        // При фокусе устанавливаем курсор на первую редактируемую позицию
        setTimeout(() => {
            if (inputRef.current) {
                const currentDigits = displayValue.replace(/\D/g, '').slice(countryCode.length);
                const firstEditPosition = countryCode === '375' ? 6 : 4;

                if (currentDigits.length === 0) {
                    inputRef.current.setSelectionRange(firstEditPosition, firstEditPosition);
                } else {
                    const nearestPos = findNearestEditablePosition(e.target.selectionStart);
                    inputRef.current.setSelectionRange(nearestPos, nearestPos);
                }
            }
        }, 0);
    };

    const handleCountryChange = (newCountryCode) => {
        setCountryCode(newCountryCode);

        // Получаем текущие цифры без кода страны
        const currentDigits = displayValue.replace(/\D/g, '').slice(countryCode.length);

        // Форматируем с новым кодом страны
        const formatted = formatPhoneNumber(currentDigits, newCountryCode);
        setDisplayValue(formatted);

        // Вызываем onChange с новым номером
        if (onChange && currentDigits.length > 0) {
            onChange(`+${newCountryCode}${currentDigits}`);
        }

        // Устанавливаем курсор на первую редактируемую позицию
        setTimeout(() => {
            if (inputRef.current) {
                const firstEditPosition = newCountryCode === '375' ? 6 : 4;
                inputRef.current.setSelectionRange(firstEditPosition, firstEditPosition);
            }
        }, 0);
    };

    const currentCountry = COUNTRIES.find(c => c.code === countryCode);

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Select
                value={countryCode}
                onChange={handleCountryChange}
                style={{ width: '150px' }}
                size="middle"
            >
                {COUNTRIES.map(country => (
                    <Option key={country.code} value={country.code}>
                        <img
                            src={country.flagImg}
                            // alt={country.name}
                            style={{
                                width: '20px',
                                height: '15px',
                                marginRight: '8px',
                                objectFit: 'cover',
                                verticalAlign: 'middle'
                            }}
                        />
                        {country.name}
                    </Option>
                ))}
            </Select>
            <input
                ref={inputRef}
                type="text"
                value={displayValue}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                onClick={handleClick}
                onFocus={handleFocus}
                className={`phone-input ${className || ''}`}
                placeholder={placeholder || currentCountry?.format}
                style={{ flex: 1 }}
                {...props}
            />
        </div>
    );
};

export default PhoneInput;